import React, { FormEvent, useCallback, useEffect, useState } from "react";
import {
	CreatedVelavuToken,
	CreateNewTokenParams,
	VelavuAPI,
	VelavuRole,
	VelavuToken,
} from "velavu-js-api";
import { useTranslation } from "react-i18next";
import IconClose from "../../dynamicicons/icon-close";
import HoverTooltip from "../../elements/hover-tooltip";
import VelavuButton from "../../elements/velavu-button";
import VelavuIconButton from "../../elements/velavu-icon-button";
import VelavuInput from "../../elements/velavu-input";
import { useVelavuModal } from "../../elements/velavu-modal";
import VelavuAlertModal from "../../elements/velavu-alert-modal/velavu-alert-modal";
import VelavuAlertModalTitle from "../../elements/velavu-alert-modal/velavu-alert-modal-title";
import VelavuAlertModalContent from "../../elements/velavu-alert-modal/velavu-alert-modal-content";
import VelavuAlertModalActions from "../../elements/velavu-alert-modal/velavu-alert-modal-actions";
import VelavuNotice from "../../elements/velavu-notice/velavu-notice";
import VelavuSelect from "../../elements/velavu-select";
import { useCancellableEffect, useToggleable } from "../../helper/hook-helper";
import { formatDate, mapUserRole } from "../../helper/language-helper";
import { getPermissionDeniedMessage } from "../../helper/permission-helper";
import * as styles from "./settings-tokens.module.scss";
import { classArr } from "../../helper/style-helper";
import * as tableStyles from "../../styles/table.module.scss";
import VelavuNoticeHeader from "../../elements/velavu-notice/velavu-notice-header";
import whitelabel from "../../whitelabel";
import VelavuNoticeBody from "../../elements/velavu-notice/velavu-notice-body";
import VelavuAlertModalObject from "../../elements/velavu-alert-modal/velavu-alert-modal-object";
import { VelavuScrollableRoundTable } from "../../elements/velavu-scrollable-round-table";
import { useHasPermission } from "../sign-in-context/sign-in-context";
import SettingsRolesTooltip from "./settings-roles-tooltip";

export default function SettingsTokens() {
	const { t } = useTranslation();
	const [tokens, setTokens] = useState<VelavuToken[]>([]);
	const { pushModal } = useVelavuModal();

	const canWriteTokens = useHasPermission("api-tokens:write");
	const canDeleteTokens = useHasPermission("api-tokens:delete");

	const addToken = useCallback(
		(token: VelavuToken) => {
			setTokens((tokens) => tokens.concat(token));
		},
		[setTokens],
	);

	const deleteToken = useCallback(
		(token: VelavuToken) => {
			pushModal<boolean>((resolve) => (
				<VelavuAlertModal>
					<VelavuAlertModalTitle>
						{t("settings.tokens.delete_title")}
					</VelavuAlertModalTitle>

					<VelavuAlertModalObject>
						{token.name}
					</VelavuAlertModalObject>

					<VelavuAlertModalContent>
						{t("settings.tokens.delete_content")}
					</VelavuAlertModalContent>

					<VelavuAlertModalActions>
						<VelavuButton
							label={t("settings.tokens.cancel")}
							variant="outlined"
							fullWidth
							onClick={() => resolve(false)}
						/>

						<VelavuButton
							label={t("settings.tokens.delete")}
							variant="danger"
							fullWidth
							onClick={() => resolve(true)}
						/>
					</VelavuAlertModalActions>
				</VelavuAlertModal>
			)).then((result) => {
				if (result) {
					VelavuAPI.apiTokens
						.deleteToken(token.id)
						.then(() => {
							setTokens((allTokens) =>
								allTokens?.filter(
									(predicateToken) =>
										predicateToken.id !== token.id,
								),
							);
						})
						.catch(console.log);
				}
			});
		},
		[pushModal, t],
	);

	useCancellableEffect((addPromise) => {
		addPromise(VelavuAPI.apiTokens.getAllTokens())
			.then(setTokens)
			.catch((error) => console.log(error.response));
	}, []);

	return (
		<div className={styles.container}>
			<ListTokens
				tokens={tokens}
				onTokenDelete={deleteToken}
				canDelete={canDeleteTokens}
			/>
			<CreateToken
				onTokenCreation={addToken}
				disabled={!canWriteTokens}
			/>
		</div>
	);
}

function ListTokens(props: {
	tokens: VelavuToken[];
	onTokenDelete: (token: VelavuToken) => void;
	canDelete?: boolean;
}) {
	const { t } = useTranslation();
	return (
		<div>
			<span className={styles.title}>{t("settings.tokens.title")}</span>
			<VelavuScrollableRoundTable
				className={styles.table}
				layout={[
					{ flex: 2.5 },
					{ flex: 2.5 },
					{ flex: 1.5 },
					{ flex: 1 },
					{ flex: 1 },
					{ flex: 1 },
					{ width: 30, alignment: "end" },
				]}
			>
				<thead className={tableStyles.scrollableTableHeader}>
					<tr>
						<th>{t("settings.tokens.table_name")}</th>
						<th>{t("settings.tokens.table_creator")}</th>
						<th>
							<span className={styles.roleHeader}>
								{t("settings.tokens.table_role")}
								<SettingsRolesTooltip />
							</span>
						</th>
						<th>{t("settings.tokens.table_created")}</th>
						<th>{t("settings.tokens.table_expiry")}</th>
						<th>{t("settings.tokens.table_status")}</th>
						<th />
					</tr>
				</thead>

				<tbody>
					{props.tokens.map((token) => (
						<TokenTableRow
							token={token}
							key={token.id}
							canDelete={props.canDelete}
							onDelete={() => props.onTokenDelete(token)}
						/>
					))}
				</tbody>
			</VelavuScrollableRoundTable>
		</div>
	);
}

function TokenStatus(props: { token: VelavuToken }) {
	const { t } = useTranslation();
	if (props.token.expiry) {
		const currentDate = new Date();
		const expiryDate = new Date(props.token.expiry);

		if (expiryDate.getTime() > currentDate.getTime()) {
			return (
				<span className={styles.active}>
					{t("settings.tokens.active")}
				</span>
			);
		} else {
			return (
				<span className={styles.inactive}>
					{t("settings.tokens.inactive")}
				</span>
			);
		}
	} else {
		return (
			<span className={styles.active}>{t("settings.tokens.active")}</span>
		);
	}
}

function TokenTableRow(props: {
	token: VelavuToken;
	canDelete?: boolean;
	onDelete: VoidFunction;
}) {
	const { t } = useTranslation();
	const [hoverState, enterHoverState, exitHoverState] = useToggleable();

	return (
		<tr
			className={styles.tableRow}
			onMouseEnter={enterHoverState}
			onMouseLeave={exitHoverState}
		>
			<td className={styles.overflow}>
				<HoverTooltip
					fixed
					tooltip={props.token.name}
					wrapperClassName={styles.overflowContent}
				>
					{props.token.name}
				</HoverTooltip>
			</td>
			<td className={styles.overflow}>
				<HoverTooltip
					fixed
					tooltip={props.token.creator}
					wrapperClassName={styles.overflowContent}
				>
					{props.token.creator}
				</HoverTooltip>
			</td>
			<td>{mapUserRole(props.token.role, t)}</td>
			<td>{formatDate(props.token.created)}</td>
			<td>
				{props.token.expiry ? formatDate(props.token.expiry) : "N/A"}
			</td>
			<td>
				<TokenStatus token={props.token} />
			</td>
			<td>
				<VelavuIconButton
					className={classArr(
						styles.tableButton,
						hoverState && styles.tableButtonHover,
					)}
					onClick={props.onDelete}
					disabled={!props.canDelete}
					tooltip={
						!props.canDelete
							? getPermissionDeniedMessage("api-tokens:delete", t)
							: undefined
					}
				>
					<IconClose color={whitelabel.palette.secondary500} />
				</VelavuIconButton>
			</td>
		</tr>
	);
}

function CreateToken(props: {
	onTokenCreation: (token: CreatedVelavuToken) => void;
	disabled?: boolean;
}) {
	const { t } = useTranslation();
	const [name, setName] = useState("");
	const [isLoading, setLoading] = useState(false);
	const [expiry, setExpiry] = useState<"none" | "week" | "month" | "3month">(
		"none",
	);
	const [role, setRole] = useState<VelavuRole>(VelavuRole.Viewer);
	const [result, setResult] = useState<
		{ success: boolean; description: string } | undefined
	>(undefined);
	const onTokenCreation = props.onTokenCreation;

	const submit = useCallback(
		(event: FormEvent<HTMLFormElement>) => {
			event.preventDefault();

			if (props.disabled) {
				return;
			}

			if (name && expiry) {
				const expiryDate = new Date();
				let body: CreateNewTokenParams;

				switch (expiry) {
					case "week":
						expiryDate.setDate(expiryDate.getDate() + 1 * 7);
						break;
					case "month":
						expiryDate.setMonth(expiryDate.getMonth() + 1);
						break;
					case "3month":
						expiryDate.setMonth(expiryDate.getMonth() + 3);
						break;
				}

				if (expiry === "none") {
					body = {
						name: name,
						role,
					};
				} else {
					body = {
						name: name,
						expiry: expiryDate.toISOString(),
						role,
					};
				}

				setLoading(true);

				VelavuAPI.apiTokens
					.createNewToken(body)
					.then((result) => {
						setResult({ success: true, description: result.token });
						setName("");
						setExpiry("none");
						setRole(VelavuRole.Viewer);
						onTokenCreation(result);
					})
					.catch((error) => {
						console.log(error.response);
						setResult({
							success: false,
							description: t("settings.tokens.create_failed", {
								name,
							}),
						});
					})
					.finally(() => {
						setLoading(false);
					});
			}
		},
		[
			expiry,
			name,
			role,
			onTokenCreation,
			setResult,
			setName,
			setLoading,
			props.disabled,
			t,
		],
	);

	useEffect(() => {
		let timeout: ReturnType<typeof setTimeout>;
		if (result) timeout = setTimeout(() => setResult(undefined), 5000);
		return () => clearTimeout(timeout);
	}, [result, setResult]);

	const fieldsDisabled = isLoading || !!props.disabled;

	return (
		<form className={styles.sectionInvite} onSubmit={submit}>
			<span className={`${styles.inviteTitle}`}>
				{t("settings.tokens.new_token_title")}
			</span>

			<div className={styles.sectionInputs}>
				<VelavuInput
					className={styles.inputTokenName}
					placeholder={t("settings.tokens.token_name_placeholder")}
					value={name}
					onChange={setName}
					disabled={fieldsDisabled}
					bottomPadding
					fullWidth
				/>
				<VelavuSelect
					value={expiry}
					onChange={setExpiry}
					disabled={fieldsDisabled}
					fullWidth
					bottomPadding
				>
					<option value="none">
						{t("settings.tokens.expiry_none")}
					</option>
					<option value="week">
						{t("settings.tokens.expiry_week")}
					</option>
					<option value="month">
						{t("settings.tokens.expiry_month")}
					</option>
					<option value="3month">
						{t("settings.tokens.expiry_3month")}
					</option>
				</VelavuSelect>
				<VelavuSelect
					value={role}
					onChange={setRole}
					disabled={fieldsDisabled}
					fullWidth
					bottomPadding
				>
					<option value={VelavuRole.Viewer}>
						{mapUserRole(VelavuRole.Viewer, t)}
					</option>
					<option value={VelavuRole.Operator}>
						{mapUserRole(VelavuRole.Operator, t)}
					</option>
					<option value={VelavuRole.SiteManager}>
						{mapUserRole(VelavuRole.SiteManager, t)}
					</option>
					<option value={VelavuRole.Admin}>
						{mapUserRole(VelavuRole.Admin, t)}
					</option>
				</VelavuSelect>
			</div>

			<VelavuButton
				label={t("settings.tokens.create")}
				disabled={fieldsDisabled || name.trim().length === 0}
				tooltip={
					props.disabled
						? getPermissionDeniedMessage("api-tokens:write", t)
						: undefined
				}
				style={{ alignSelf: "flex-end" }}
				submit
			/>

			{result && (
				<VelavuNotice
					className={styles.statusNotice}
					type={result.success ? "confirm" : "error"}
				>
					<VelavuNoticeHeader>
						{result.success
							? t("settings.tokens.created_title")
							: t("settings.tokens.created_failed_title")}
					</VelavuNoticeHeader>

					<VelavuNoticeBody>{result.description}</VelavuNoticeBody>
				</VelavuNotice>
			)}
		</form>
	);
}
